// Store de módulo (singleton) para la resolución de cada feature. Existe para que
// TODAS las instancias del hook `useFeatureVersion(featureKey)` compartan UN solo estado:
// el gate del router (que decide qué página renderizar) y la card de invitación viven en
// instancias separadas del hook. Sin estado compartido, el switch optimista de la card no
// re-renderizaba el gate y la página seguía en V1 hasta navegar/recargar (defecto funcional).
//
// `useSyncExternalStore` consume este store: cualquier write notifica a TODOS los
// suscriptores del mismo featureKey en el mismo tick, así el flip de variante re-monta el
// subtree del menú al instante (corrección VINCULANTE #6 del plan, sin navigate ni reload).

import { readCache, writeCache } from './cache';
import { scheduleReconcile } from './reconcilers';
import type { FeatureVariant, FeatureVersionCacheEntry } from './types';

export interface FeatureVersionState {
  entry: FeatureVersionCacheEntry;
  // `resolved` = ya hidratamos desde el backend al menos una vez en esta sesión.
  resolved: boolean;
}

type Listener = () => void;

const controlFallbackEntry = (featureKey: string): FeatureVersionCacheEntry => ({
  feature_key: featureKey,
  variant: 'control',
  can_revert: false,
  ga_forced: false,
  menu_state: null,
  experiment_id: null,
  // Sin resolución todavía: no invitar PUSH ni mostrar barra de peek. El chip PULL queda
  // oculto hasta que el backend confirme manual_entry='available' al hidratar.
  invite_surface: 'none',
  manual_entry: 'hidden',
  next_eligible_at: null,
  operator_evacuated: false,
  // Default robusto: sin ramp-down activo el motivo no se usa, pero mantenemos 'iterating'
  // (temporal) como valor seguro coherente con la normalización del cache/hook.
  evac_reason: 'iterating',
  compare_active: false,
  // Sin resolución no hay catálogo de chips: null → defaults de strings.ts.
  feedback_options: null,
  updated_at: 0,
  // Sin backend todavía: no confirmado. La hidratación autoritativa lo sellará al resolver.
  confirmed_at: null,
});

// Un slot de estado + sus listeners por featureKey. El snapshot es estable entre renders
// (misma referencia) salvo que un write lo reemplace → useSyncExternalStore no parpadea.
interface Slot {
  state: FeatureVersionState;
  listeners: Set<Listener>;
}

const slots = new Map<string, Slot>();

function getSlot(featureKey: string): Slot {
  let slot = slots.get(featureKey);
  if (!slot) {
    // Inicialización SÍNCRONA desde cache local (0ms): el router decide la variante sin esperar
    // red para que la UX no parpadee. Si no hay cache → control.
    const cached = readCache(featureKey);
    slot = {
      state: {
        entry: cached ?? controlFallbackEntry(featureKey),
        // INVARIANTE CRÍTICA (raíz del bug "V2 en pantalla / control en BD"): `resolved` NO se
        // marca true solo por tener cache. El cache hidrata la UX al instante, pero el GET de
        // reconciliación DEBE correr una vez por sesión y el backend GANA. Si marcáramos
        // resolved=Boolean(cached), un cache stale (treatment optimista cuyo PUT se perdió)
        // bloquearía la hidratación (useFeatureVersion early-return) y el FE quedaría en V2
        // de forma DURABLE mientras la BD dice control. `resolved` solo se vuelve true tras un
        // GET real (éxito o fallo de red controlado), nunca por la mera presencia de cache.
        resolved: false,
      },
      listeners: new Set(),
    };
    slots.set(featureKey, slot);
  }
  return slot;
}

// Snapshot estable (misma referencia mientras no haya write) para useSyncExternalStore.
export function getStoreSnapshot(featureKey: string): FeatureVersionState {
  return getSlot(featureKey).state;
}

export function subscribeStore(featureKey: string, listener: Listener): () => void {
  const slot = getSlot(featureKey);
  slot.listeners.add(listener);
  return () => {
    slot.listeners.delete(listener);
  };
}

function notify(slot: Slot): void {
  slot.listeners.forEach((l) => l());
}

// Reemplaza el snapshot solo si algo cambió (igualdad campo a campo) y notifica a todas
// las instancias del hook. Devuelve true si hubo cambio efectivo.
export function setStoreEntry(featureKey: string, next: FeatureVersionCacheEntry): boolean {
  const slot = getSlot(featureKey);
  const prev = slot.state.entry;
  const unchanged =
    prev.variant === next.variant &&
    prev.can_revert === next.can_revert &&
    prev.ga_forced === next.ga_forced &&
    prev.menu_state === next.menu_state &&
    prev.experiment_id === next.experiment_id &&
    prev.invite_surface === next.invite_surface &&
    prev.manual_entry === next.manual_entry &&
    prev.next_eligible_at === next.next_eligible_at &&
    prev.operator_evacuated === next.operator_evacuated &&
    prev.evac_reason === next.evac_reason &&
    prev.compare_active === next.compare_active &&
    // feedback_options es un objeto re-creado en cada normalización (readCache/hidratación):
    // comparar por referencia daría "cambió" en cada GET aunque el catálogo sea idéntico →
    // notificaciones extra a todos los suscriptores. Igualdad por JSON: es determinista porque
    // la normalización emite siempre las claves en el mismo orden (revert, permanence).
    JSON.stringify(prev.feedback_options) === JSON.stringify(next.feedback_options) &&
    // confirmed_at participa de la igualdad: un acuse del backend que solo sella la confirmación
    // (misma variante, optimista→confirmado) debe persistir en el snapshot para que el outbox no
    // lo confunda con pendiente en un próximo ciclo. No afecta render, pero sí la lógica de revert.
    prev.confirmed_at === next.confirmed_at;
  if (unchanged) return false;
  slot.state = { entry: next, resolved: slot.state.resolved };
  notify(slot);
  return true;
}

export function setStoreResolved(featureKey: string, resolved: boolean): void {
  const slot = getSlot(featureKey);
  if (slot.state.resolved === resolved) return;
  slot.state = { entry: slot.state.entry, resolved };
  notify(slot);
}

// Reset de PRODUCCIÓN: limpia el singleton al cambiar de sesión/sucursal (login, logout,
// refreshLogin). Sin esto, el `slots` de módulo (keyed solo por featureKey) conserva la
// resolución del comercio anterior; como queda `resolved: true`, el hook salta la re-hidratación
// y muestra la variante equivocada al nuevo comercio (multisucursal cambia de sesión SIN reload).
//
// Notifica a los suscriptores montados antes de vaciar para que re-lean el snapshot: el próximo
// getSlot reinicializa SÍNCRONO desde el cache local del comercio activo (keyed por commerce_id),
// y `resolved` arranca según ese cache → re-hidrata si hace falta.
export function resetFeatureVersionStore(): void {
  const listeners: Listener[] = [];
  slots.forEach((slot) => slot.listeners.forEach((l) => listeners.push(l)));
  slots.clear();
  listeners.forEach((l) => l());
}

// Revierte un switch optimista que el backend RECHAZÓ (REQUIRES_ROOT) o cuyo PUT se AGOTÓ tras
// MAX_ATTEMPTS sin tragárselo. Sin esto el FE queda en "V2 fantasma": variante treatment en
// pantalla que la BD nunca registró. La reversión elige el destino así:
//   1. Si confirmed_at es número (el backend ya había confirmado una variante) → vuelve a ESA
//      variante autoritativa, conservando confirmed_at (no perdemos lo que el backend sí confirmó,
//      p.ej. un treatment de auto-hook que el comercio luego intentó revertir y el revert falló).
//   2. Si confirmed_at es null (optimista sin confirmar) → vuelve al `fromVariant` del item que
//      falló: la variante que existía JUSTO ANTES de ESE switch. Esto evita el race de switches
//      rápidos (Seer 14511026): si el usuario hizo A→B y luego B→A antes de que el PUT de A→B
//      fallara, leer el store daría la variante de la 2ª acción; usar el from_variant del item
//      ancla la reversión al switch real que se perdió, no al estado superpuesto. Si el item no
//      trae from_variant (no debería en un switch de variante) → `control` (único valor seguro).
// En todos los casos marca resolved=false para forzar re-hidratación: el GET autoritativo dirá la
// última palabra (si el backend SÍ tiene treatment —p.ej. forced_to_treatment— lo restaurará),
// así cualquier divergencia temporal del race se autocorrige en el mismo ciclo de sesión.
//
// `commerceId` permite revertir el cache del comercio correcto desde un drain en background, donde
// el id_commerce activo pudo cambiar (multisucursal). El store de módulo es per-featureKey, así que
// solo notificamos si el comercio del store activo coincide con el que se está revirtiendo.
export function revertOptimisticEntry(
  featureKey: string,
  opts: { commerceId: string | null; activeCommerceId: string | null; fromVariant?: FeatureVariant | null },
): void {
  const slot = getSlot(featureKey);
  const current = slot.state.entry;
  const confirmedVariant: FeatureVariant =
    current.confirmed_at !== null ? current.variant : (opts.fromVariant ?? 'control');
  const reverted: FeatureVersionCacheEntry = {
    ...current,
    variant: confirmedVariant,
    // Sin respaldo del backend no podemos afirmar can_revert; control nunca tiene revert pendiente.
    can_revert: confirmedVariant === 'treatment' ? current.can_revert : false,
    compare_active: false,
    confirmed_at: current.confirmed_at,
    updated_at: Date.now(),
  };

  // Persistir en el cache del comercio que generó el switch (puede no ser el activo).
  writeCache(reverted, opts.commerceId ?? undefined);

  // Solo tocar el store de módulo (UI viva) si el comercio revertido es el que está en pantalla.
  // Si el operador ya cambió de sucursal, el cache corregido basta: el próximo getSlot lo re-leerá.
  if (opts.commerceId !== null && opts.commerceId !== opts.activeCommerceId) return;

  const changed = setStoreEntry(featureKey, reverted);
  // Forzar re-hidratación autoritativa aunque la variante no haya cambiado (p.ej. ya estaba en
  // control): el GET es la última palabra y debe correr para sellar/corregir confirmed_at.
  setStoreResolved(featureKey, false);
  if (!changed) notify(slot);
  // Reconciliación (SPEC-11): cause='optimistic_rollback' — NO es decisión del comercio (es el
  // rebote REQUIRES_ROOT/agotado deshaciendo un optimista). Los reconciliadores con efectos de
  // intención (p.ej. el reset BLE) lo IGNORAN; se agenda igual por completitud del contrato.
  scheduleReconcile(featureKey, { entry: reverted, prev: current, cause: 'optimistic_rollback' });
}

// Solo para tests: limpia el singleton entre casos.
export function __resetStoreForTests(): void {
  slots.clear();
}
